<template>
  <v-card outlined>
    <Create
      :createDialog="createDialog"
      @close="createDialog = !createDialog"
      @submit="getList"
    ></Create>
    <Edit
      :editDialog="editDialog"
      @close="editDialog = !editDialog"
      @submit="getList"
      :id="editId"
    ></Edit>

    <v-toolbar dense flat :color="resolvedColor">
      <v-app-bar-nav-icon @click="$router.go(-1)" v-if="!hideBackBtn">
        <v-icon>mdi-chevron-left</v-icon>
      </v-app-bar-nav-icon>

      <v-toolbar-title>Delivery </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn @click="add" outlined rounded raised small>
        <v-icon small>add</v-icon> New
      </v-btn>
    </v-toolbar>

    <v-toolbar dense elevation="0">
      <!-- Show btn-toggle on md+ -->
      <div v-if="!$vuetify.breakpoint.smAndDown">
        <span>Status:</span>
        <v-btn-toggle v-model="deliveryStatus" @change="getList()" small group>
          <v-btn value="all">
            <span>All</span>
          </v-btn>
          <v-btn value="pending">
            <span>Pending</span>
          </v-btn>
          <v-btn value="delivered">
            <span>Delivered</span>
          </v-btn>
        </v-btn-toggle>
      </div>

      <!-- Show select on sm and down -->
      <v-select
        v-else
        v-model="deliveryStatus"
        :items="[
          { text: 'All', value: 'all' },
          { text: 'Pending', value: 'pending' },
          { text: 'Delivered', value: 'delivered' },
        ]"
        dense
        hide-details
        outlined
        style="max-width: 150px"
        @change="getList()"
      />

      <v-spacer></v-spacer>

      <DateTimePicker
        :defaultRange="defaultRange"
        :range="true"
        :hideTime="true"
        v-model="dateTime"
        @submit="getList()"
      />
    </v-toolbar>
    <v-divider></v-divider>
    <v-card-text>
      <v-expansion-panels multiple inset>
        <v-expansion-panel
          v-for="(group, index) in groupedDelivery"
          :key="index"
        >
          <v-expansion-panel-header>
            <div class="d-flex justify-space-between align-center w-100">
              <div>
                {{ $moment(group.date).format("dddd, LL") }}
                <span class="grey--text"
                  >({{ group.items.length }} Delivery)</span
                >
              </div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Delivery #</th>
                  <th>Name</th>
                  <th>Time</th>
                  <th>Status</th>
                  <th class="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in group.items" :key="item.id" class="pointer">
                  <td>{{ item.deliveryOrder }}</td>
                  <td>
                    <div>{{ item.name }}</div>
                    <div v-if="item.trackingNumber">
                      Tracking #{{ item.trackingNumber }}
                    </div>
                  </td>
                  <td>
                    <div>
                      {{ formatTime(item.startTime) }} -
                      {{ formatTime(item.endTime) }}
                    </div>
                    <div>{{ item.deliveryType }}</div>
                  </td>
                  <td class="text-capitalize">
                    <v-icon small class="mr-1">
                      {{
                        item.status === "pending"
                          ? "mdi-account-clock-outline"
                          : "mdi-check-outline"
                      }}
                    </v-icon>
                    {{ item.status }}
                  </td>
                  <td class="text-right">
                    <v-menu bottom left offset-y>
                      <template v-slot:activator="{ on }">
                        <v-btn v-on="on" icon>
                          <v-icon>more_vert</v-icon>
                        </v-btn>
                      </template>
                      <v-list class="menu-button">
                        <v-list-item
                          v-if="item.status === 'pending'"
                          @click="changeStatus(item, 'delivered')"
                        >
                          <v-list-item-title>Mark Delivered</v-list-item-title>
                        </v-list-item>
                        <v-list-item
                          v-else
                          @click="changeStatus(item, 'pending')"
                        >
                          <v-list-item-title>Mark Pending</v-list-item-title>
                        </v-list-item>
                        <v-divider></v-divider>
                        <v-list-item @click="editDelivery(item)">
                          <v-list-item-title>Edit</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="deleteDelivery(item)">
                          <v-list-item-title>Delete</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
  </v-card>
</template>

<script>
import deliveryService from "@/modules/Delivery/service.js";
const DateTimePicker = () => import("@/components/DateTimePicker");
const Create = () => import("./Create");
const Edit = () => import("./Edit");

export default {
  props: {
    hideBackBtn: {
      default: false,
    },
    id: {
      default: 0,
    },
    autofocus: {
      default: true,
    },
    color: {
      color: {
        type: String,
        default: null, // allow it to be set later
      },
    },
  },
  data() {
    return {
      editId: null,
      createDialog: false,
      editDialog: false,
      deliveryStatus: "all",
      search: "",
      loading: true,
      delivers: [],
      page: 1,
      sortBy: "name",
      pageCount: 0,
      itemsPerPage: 10,
      headers: [
        {
          text: "Delivery #",
          align: "left",
          value: "deliveryOrder",
          width: 150,
          sortable: true,
        },
        {
          text: "Name",
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Delivery Date",
          align: "left",
          value: "deliveryDate",
          sortable: true,
        },
        {
          text: "Time",
          sortable: false,
        },
        {
          text: "Status",
          sortable: true,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        },
      ],
      dateTime: {
        start: this.$moment().startOf("day").toISOString(),
        end: this.$moment().add(15, "days").endOf("day").toISOString(),
      },
      defaultRange: "next15Days",
    };
  },
  created() {
    this.getList();
  },
  components: {
    Create,
    DateTimePicker,
    Edit,
  },
  computed: {
    resolvedColor() {
      return this.color || this.$style.purchase.titlebar;
    },
    groupedDelivery() {
      const groups = {};
      this.delivers.forEach((item) => {
        // Force UTC interpretation to avoid timezone shift
        const dateKey = this.$moment
          .utc(item.deliveryDate)
          .format("YYYY-MM-DD");
        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(item);
      });

      return Object.keys(groups)
        .map((date) => ({
          date,
          items: groups[date],
        }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    },
  },
  methods: {
    changeStatus(item, status) {
      this.$swal({
        title: "Are you sure?",
        text: `You you sure? You want to change delivery status to ${status}!`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          this.isLoading = true;
          let data = {
            status: status,
          };
          return deliveryService
            .update(item.id, data)
            .then(() => {
              // this.getSubscription();

              this.isLoading = false;
              this.getList();
              // console.log("updateCustomer", result);
            })
            .catch((error) => {
              console.log("error", error);
            });
        }
      });
    },
    editDelivery(item) {
      this.editId = item.id;
      this.editDialog = !this.editDialog;
    },
    deleteDelivery(item) {
      this.$swal({
        title: "Are you sure?",
        text: "You want to mark this order delivered!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          deliveryService.update(item.id).then((response) => {
            if (response.data) {
              this.getList();
            }
          });
        }
      });
    },
    formatTime(time) {
      if (!time) return "Not Set";
      return this.$moment(time, "HH:mm").format("hh:mm A");
    },
    add() {
      this.createDialog = !this.createDialog;
    },
    getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;
      let where = {
        start: this.dateTime.start,
        end: this.dateTime.end,
        status: this.deliveryStatus === "all" ? null : this.deliveryStatus,
      };

      return deliveryService.getAll(where).then((response) => {
        this.loading = false;
        this.delivers = response.data;
      });
    },
  },
};
</script>